import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createProjectState,
  deleteProjectState,
  getProjectStates,
  reapplyProjectTemplate,
  reorderProjectStates,
  updateProjectState,
  type CreateProjectStateInput,
  type ReapplyResult,
  type TaskState,
  type UpdateProjectStateInput,
} from '../services/api/projectStates';

/**
 * Fetch the ordered state list for a single project (Phase 19, D-10).
 *
 * Each project section in `TaskManagementPage.tsx` (plan 19-07) calls this
 * with its own `projectId`. The cache key is `['projectStates', projectId]`
 * so each section is independently cached, fetched once per mount, and
 * revalidated by mutations that invalidate this key (plan 19-04's
 * `useSetTaskState` does NOT need to invalidate states, only `managerTasks`,
 * because state names/colors don't change as a side-effect of moving a
 * task — that's a Phase 20 concern.)
 */
export function useProjectStates(projectId: number | undefined) {
  return useQuery({
    queryKey: ['projectStates', projectId],
    queryFn: () => getProjectStates(projectId!),
    enabled: !!projectId,
  });
}

export type { TaskState };

// ===== Phase 20 additions: per-project state CRUD + reorder + re-apply mutations =====

/**
 * Admin/PM: create a state on a project (PROJ-03, D-08 per-row commit).
 * Optimistic — appends a synthetic-id row at the end, replaced on settled.
 * Note: synthetic ids are NEGATIVE; plan 20-08's handleReorderStates
 * race-guards against negative ids so they never hit the reorder endpoint.
 */
export function useCreateProjectState(projectId: number) {
  const queryClient = useQueryClient();
  return useMutation<
    TaskState,
    Error,
    CreateProjectStateInput,
    { previous: TaskState[] | undefined }
  >({
    mutationFn: (input) => createProjectState(projectId, input),
    onMutate: async (input) => {
      const key = ['projectStates', projectId];
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<TaskState[]>(key);
      if (previous) {
        const synthetic: TaskState = {
          // WR-05: include `previous.length` so two rapid clicks within the
          // same millisecond can't collide (matches the pattern used in
          // ProjectTemplateEditPage:98). Stays negative — replaced by the
          // real id on settled.
          id: -Date.now() - previous.length,
          projectId,
          name: input.name,
          // User UAT 2026-06-10 — use the ordering the caller sent so the
          // optimistic row matches what the server will persist. Falls back
          // to `previous.length` for legacy callers that omit ordering
          // (defensive — TS now requires the field on CreateProjectStateInput
          // but other JS-side callers may exist).
          ordering: input.ordering ?? previous.length,
          color: input.color,
          isTerminal: input.isTerminal ?? false,
          isInProgress: input.isInProgress ?? false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        queryClient.setQueryData<TaskState[]>(key, [...previous, synthetic]);
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(['projectStates', projectId], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['projectStates', projectId] });
    },
  });
}

/**
 * Admin/PM: rename or recolor a state (PROJ-03, D-08, D-12).
 * Optimistic — patches matching row in-place.
 */
export function useUpdateProjectState(projectId: number) {
  const queryClient = useQueryClient();
  return useMutation<
    TaskState,
    Error,
    { stateId: number; patch: UpdateProjectStateInput },
    { previous: TaskState[] | undefined }
  >({
    mutationFn: ({ stateId, patch }) => updateProjectState(projectId, stateId, patch),
    onMutate: async ({ stateId, patch }) => {
      const key = ['projectStates', projectId];
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<TaskState[]>(key);
      if (previous) {
        queryClient.setQueryData<TaskState[]>(
          key,
          previous.map((s) => (s.id === stateId ? { ...s, ...patch } : s))
        );
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(['projectStates', projectId], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['projectStates', projectId] });
    },
  });
}

/**
 * Admin/PM: delete a state (PROJ-03, STATE-04, D-20).
 * Optimistic remove — rolls back on 409 STATE_IN_USE so the editor row
 * reappears while AppToast shows the deep-link to the offending tasks.
 */
export function useDeleteProjectState(projectId: number) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, number, { previous: TaskState[] | undefined }>({
    mutationFn: (stateId) => deleteProjectState(projectId, stateId),
    onMutate: async (stateId) => {
      const key = ['projectStates', projectId];
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<TaskState[]>(key);
      if (previous) {
        queryClient.setQueryData<TaskState[]>(
          key,
          previous.filter((s) => s.id !== stateId)
        );
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(['projectStates', projectId], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['projectStates', projectId] });
    },
  });
}

/**
 * Admin/PM: persist a drag-drop reorder (PROJ-03, D-13).
 * Optimistic — rewrites array order AND each state's `ordering` index.
 */
export function useReorderProjectStates(projectId: number) {
  const queryClient = useQueryClient();
  return useMutation<
    TaskState[],
    Error,
    number[],
    { previous: TaskState[] | undefined }
  >({
    mutationFn: (stateIds) => reorderProjectStates(projectId, stateIds),
    onMutate: async (stateIds) => {
      const key = ['projectStates', projectId];
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<TaskState[]>(key);
      if (previous) {
        const byId = new Map(previous.map((s) => [s.id, s]));
        const reordered = stateIds
          .map((id, idx) => {
            const s = byId.get(id);
            return s ? { ...s, ordering: idx } : null;
          })
          .filter((s): s is TaskState => s !== null);
        queryClient.setQueryData<TaskState[]>(key, reordered);
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(['projectStates', projectId], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['projectStates', projectId] });
    },
  });
}

/**
 * Admin-only: re-apply a template (PROJ-04, D-18).
 *
 * Resolves to `ReapplyResult = { added, updated, removed }` per backend
 * `reapplyResponseSchema`. The caller (ReapplyTemplateModal) does NOT
 * consume the value — it only relies on the cache invalidation below
 * to refresh the editor + task list + project header.
 *
 * Simple invalidate-only — no optimistic update because the diff modal
 * needs the authoritative post-apply state list. Invalidates THREE keys
 * on success:
 *   - ['projectStates', projectId] — editor reflects the new states (D-18).
 *   - ['managerTasks']             — task list chips re-render (D-18).
 *   - ['project', projectId]        — single-project cache so plan 20-08's
 *                                     "Most recently applied: X" header
 *                                     picks up the new appliedTemplateId.
 *                                     Key matches useProject() in
 *                                     frontend/src/hooks/useProjects.ts:92.
 *
 * On 409 STATE_IN_USE the thrown error carries `details.conflicts`;
 * the caller keeps the modal open and surfaces inline conflict badges
 * per D-19.
 */
export function useReapplyProjectTemplate(projectId: number) {
  const queryClient = useQueryClient();
  return useMutation<ReapplyResult, Error, number>({
    mutationFn: (projectTemplateId: number) =>
      reapplyProjectTemplate(projectId, projectTemplateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projectStates', projectId] });
      queryClient.invalidateQueries({ queryKey: ['managerTasks'] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
  });
}
